import React, { useEffect, useRef, useState } from 'react';
import { Spin, Alert } from 'antd';
import styled from 'styled-components';

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
`;

const VideoCanvas = styled.canvas`
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
  image-rendering: crisp-edges;
  image-rendering: -webkit-optimize-contrast;
`;

const StatusOverlay = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 12px;
  border-radius: 12px;
  color: white;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$connected ? '#52c41a' : '#ff4d4f'};
  animation: ${props => props.$connected ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: white;
  z-index: 100;
  pointer-events: none;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
`;

const WebSocketVideo = ({ cameraId, detection = true, fps = 20, style }) => {
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    const connectWebSocket = () => {
      try {
        setLoading(true);
        setError(null);

        // WebSocket URL (use API_BASE_URL to ensure correct backend connection)
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const wsProtocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
        const wsHost = apiUrl.replace(/^https?:\/\//, ''); // Remove http:// or https://
        const wsUrl = `${wsProtocol}//${wsHost}/ws/video/${cameraId}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.binaryType = 'arraybuffer';

        ws.onopen = () => {
          console.log(`WebSocket connected for camera ${cameraId}`);
          setConnected(true);
          setLoading(false);
          setError(null);
          reconnectAttemptsRef.current = 0;

          // Send initial configuration
          ws.send(JSON.stringify({ command: 'detection', value: detection }));
          ws.send(JSON.stringify({ command: 'fps', value: fps }));
        };

        ws.onmessage = async (event) => {
          if (typeof event.data === 'string') {
            // JSON message (error or status)
            try {
              const data = JSON.parse(event.data);
              if (data.type === 'error') {
                console.error('WebSocket error:', data.message);
                setError(data.message);
              }
            } catch (e) {
              console.error('Failed to parse WebSocket message:', e);
            }
          } else {
            // Binary frame data - use ImageBitmap for faster rendering
            const blob = new Blob([event.data], { type: 'image/jpeg' });

            try {
              // ImageBitmap API is much faster than Image element
              const imageBitmap = await createImageBitmap(blob);

              const canvas = canvasRef.current;
              if (canvas) {
                const ctx = canvas.getContext('2d');

                // Only update canvas size if dimensions changed
                if (canvas.width !== imageBitmap.width || canvas.height !== imageBitmap.height) {
                  canvas.width = imageBitmap.width;
                  canvas.height = imageBitmap.height;
                }

                ctx.drawImage(imageBitmap, 0, 0);
              }

              // Clean up ImageBitmap
              imageBitmap.close();
            } catch (e) {
              console.error('Failed to decode frame:', e);
            }
          }
        };

        ws.onerror = (error) => {
          console.error(`WebSocket error for camera ${cameraId}:`, error);
          setError('Connection error');
        };

        ws.onclose = () => {
          console.log(`WebSocket closed for camera ${cameraId}`);
          setConnected(false);

          // Auto-reconnect with exponential backoff
          if (reconnectAttemptsRef.current < 5) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
            console.log(`Reconnecting in ${delay}ms...`);

            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current += 1;
              connectWebSocket();
            }, delay);
          } else {
            setError('Connection lost. Too many reconnection attempts.');
            setLoading(false);
          }
        };

      } catch (err) {
        console.error('Failed to connect WebSocket:', err);
        setError('Failed to connect to camera');
        setLoading(false);
      }
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [cameraId, detection, fps]);

  // Update settings when props change
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ command: 'detection', value: detection }));
    }
  }, [detection]);

  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ command: 'fps', value: fps }));
    }
  }, [fps]);

  return (
    <VideoContainer style={style}>
      <VideoCanvas ref={canvasRef} />

      {!loading && !error && (
        <StatusOverlay>
          <StatusDot $connected={connected} />
          {connected ? 'Live' : 'Disconnected'}
        </StatusOverlay>
      )}

      {loading && (
        <LoadingOverlay>
          <Spin size="large" />
          <div>Connecting to camera...</div>
        </LoadingOverlay>
      )}

      {error && !loading && (
        <ErrorOverlay>
          <Alert
            message="Camera Error"
            description={error}
            type="error"
            showIcon
          />
        </ErrorOverlay>
      )}
    </VideoContainer>
  );
};

export default WebSocketVideo;
